"use client";

import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { Z_INDEX } from "./z-index";

const drawerVariants = cva(
  "fixed flex flex-col bg-white shadow-[0_0_0_1px_rgba(15,23,42,0.06),0_18px_48px_-14px_rgba(15,23,42,0.18)] dark:bg-zinc-900 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_18px_48px_-14px_rgba(0,0,0,0.5)]",
  {
    variants: {
      side: {
        right: "inset-y-0 right-0 h-full w-full max-w-md rounded-l-2xl",
        left: "inset-y-0 left-0 h-full w-full max-w-md rounded-r-2xl",
        bottom: "inset-x-0 bottom-0 h-auto max-h-[85vh] w-full rounded-t-2xl",
      },
      size: {
        sm: "",
        md: "",
        lg: "",
        full: "",
      },
    },
    compoundVariants: [
      { side: "right", size: "sm", className: "max-w-sm" },
      { side: "right", size: "md", className: "max-w-md" },
      { side: "right", size: "lg", className: "max-w-2xl" },
      { side: "right", size: "full", className: "max-w-full" },
      { side: "left", size: "sm", className: "max-w-sm" },
      { side: "left", size: "md", className: "max-w-md" },
      { side: "left", size: "lg", className: "max-w-2xl" },
      { side: "left", size: "full", className: "max-w-full" },
    ],
    defaultVariants: {
      side: "right",
      size: "md",
    },
  }
);

export interface DrawerProps extends VariantProps<typeof drawerVariants> {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: string;
  children: React.ReactNode;
  /** Fixed footer pinned to the bottom of the panel (e.g. action buttons). Stays put while the content scrolls. */
  footer?: React.ReactNode;
  className?: string;
}

/**
 * v2 side Drawer. Renders a full-viewport blur backdrop (`Z_INDEX.DRAWER_BACKDROP`)
 * + a sliding panel (`Z_INDEX.DRAWER` = 10800). Controlled via `open` / `onClose`.
 *
 * ── Usage notes (read before using inside complex screens) ──────────────────
 *
 * 1) Radix popovers inside the Drawer (v2 `Select`, `Popover`, `DropdownMenu`,
 *    `Tooltip`): Radix mounts those in a `[data-radix-popper-content-wrapper]`
 *    whose `z-index` defaults to `auto`, so the popup would paint BEHIND this
 *    drawer (no z-index on the *inner* content can escape the wrapper's stacking
 *    context). This is handled globally in `src/styles/global.css`, which lifts
 *    that wrapper to `z-index: 11990` (above drawers + modals, below toasts).
 *    => v2 `Select` etc. work inside a Drawer out of the box. Do NOT remove that
 *    global rule.
 *
 * 2) This component does NOT portal itself — it relies on `position: fixed`. If
 *    any ancestor has a CSS `transform` (e.g. an entrance-animated v2 `Card`),
 *    `fixed` becomes relative to that ancestor and the backdrop won't cover the
 *    viewport. In that case mount it via a portal:
 *      `{open && typeof document !== "undefined" &&
 *        createPortal(<Drawer open onClose={...}>…</Drawer>, document.body)}`
 *    See `ReceivedRecognitionsItemCard` / `CertificationCard` for examples.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  side = "right",
  size = "md",
  className,
}: DrawerProps) {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in-0"
        style={{ zIndex: Z_INDEX.DRAWER_BACKDROP }}
        onClick={onClose}
        aria-hidden
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        className={cn(
          drawerVariants({ side, size }),
          "motion-safe:animate-in motion-safe:duration-300",
          side === "right" && "motion-safe:slide-in-from-right",
          side === "left" && "motion-safe:slide-in-from-left",
          side === "bottom" && "motion-safe:slide-in-from-bottom",
          className
        )}
        style={{ zIndex: Z_INDEX.DRAWER }}
      >
        <div className="flex items-start justify-between border-b border-slate-200/70 p-4 dark:border-zinc-800">
          <div className="space-y-1">
            {title && (
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {title}
              </h2>
            )}
            {description && (
              <p className="text-sm text-slate-600 dark:text-zinc-400">
                {description}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">{children}</div>

        {/* Fixed footer (optional) */}
        {footer && (
          <div className="shrink-0 border-t border-slate-200/70 px-4 py-2.5 dark:border-zinc-800">
            {footer}
          </div>
        )}
      </div>
    </>
  );
}
